// ============================================================================
// STAKEHOLDER PEOPLE TAB
// Full CRUD for contact people inside WorkItemDetailModal.
// Inline add/edit form panel with all fields from the legacy PersonModal.
// Date: March 2026
// ============================================================================

'use client';

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Users,
  Search,
  Plus,
  Mail,
  MapPin,
  Star,
  Save,
  Loader2,
  Trash2,
  ChevronLeft,
  Camera,
  Edit,
  Send,
  RefreshCw,
  CheckCircle,
  X,
} from 'lucide-react';
import type { WorkItem } from '../UniversalWorkItems';
import { PhoneDisplayWithWhatsApp } from '@/components/contacts/PhoneInputWithCountryCode';
import PhoneInputWithCountryCode from '@/components/contacts/PhoneInputWithCountryCode';
import { useContactConfig } from '@/hooks/useContactConfig';
import { authService } from '@/lib/authService';
import { notify, confirm } from '@/lib/notifications';
import { validateWhatsAppNumber } from '@/lib/whatsappService';
import { WebQRScanner } from '@/components/shared/WebQRScanner';

// Static file server config
const STATIC_FILE_SERVER_BASE = 'https://staticfileserver.totalaccess.co.za';
const STATIC_FILE_API_KEY = process.env.NEXT_PUBLIC_STATIC_FILE_SERVER_API_KEY || 'a11abd7b-0d47-4fe2-a66f-f7c405cf5f80';

const getAuthToken = () => authService.getTokens()?.access_token || null;

/**
 * Extract WhatsApp invite code from a link.
 * Supports: https://chat.whatsapp.com/XXXX, https://chat.whatsapp.com/XXXX?s=...
 * Also accepts raw invite codes.
 */
function extractWhatsAppInviteCode(link: string): string | null {
  const trimmed = link.trim();
  if (!trimmed) return null;

  // If it's already just a code (no URL), validate it's alphanumeric with some length
  if (!trimmed.includes('/')) {
    const code = trimmed.replace(/[^a-zA-Z0-9_-]/g, '');
    return code.length >= 5 ? code : null;
  }

  // Parse URL
  try {
    const url = new URL(trimmed);
    const pathParts = url.pathname.split('/').filter(Boolean);
    const lastPart = pathParts[pathParts.length - 1];
    if (lastPart && lastPart.length >= 5) {
      return lastPart;
    }
  } catch {
    // Not a valid URL — try regex fallback
    const match = trimmed.match(/chat\.whatsapp\.com\/([a-zA-Z0-9_-]+)/);
    if (match) return match[1];
  }

  return null;
}

/** Build an immutable OnRouteNotifications update with proper type safety. */
function buildOnRouteNotifications(
  prev?: ContactPersonNotifications,
  updates?: Partial<OnRouteNotifications>
): ContactPersonNotifications {
  return {
    on_route: {
      whatsapp: prev?.on_route?.whatsapp ?? false,
      email: prev?.on_route?.email ?? false,
      whatsapp_group_jid: prev?.on_route?.whatsapp_group_jid ?? null,
      whatsapp_group_invite_link: prev?.on_route?.whatsapp_group_invite_link ?? null,
      ...updates,
    },
    on_site: {
      whatsapp: prev?.on_site?.whatsapp ?? false,
      email: prev?.on_site?.email ?? false,
      whatsapp_group_jid: prev?.on_site?.whatsapp_group_jid ?? null,
      whatsapp_group_invite_link: prev?.on_site?.whatsapp_group_invite_link ?? null,
    },
  };
}

/** Build an immutable OnSiteNotifications update with proper type safety. */
function buildOnSiteNotifications(
  prev?: ContactPersonNotifications,
  updates?: Partial<OnSiteNotifications>
): ContactPersonNotifications {
  return {
    on_route: {
      whatsapp: prev?.on_route?.whatsapp ?? false,
      email: prev?.on_route?.email ?? false,
      whatsapp_group_jid: prev?.on_route?.whatsapp_group_jid ?? null,
      whatsapp_group_invite_link: prev?.on_route?.whatsapp_group_invite_link ?? null,
    },
    on_site: {
      whatsapp: prev?.on_site?.whatsapp ?? false,
      email: prev?.on_site?.email ?? false,
      whatsapp_group_jid: prev?.on_site?.whatsapp_group_jid ?? null,
      whatsapp_group_invite_link: prev?.on_site?.whatsapp_group_invite_link ?? null,
      ...updates,
    },
  };
}

async function uploadImage(file: File, folder: string): Promise<string | null> {
  try {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder', folder);
    const res = await fetch(`${STATIC_FILE_SERVER_BASE}/upload`, {
      method: 'POST',
      headers: { 'X-API-Key': STATIC_FILE_API_KEY },
      body: fd,
    });
    if (res.ok) {
      const data = await res.json();
      return data.directUrl || data.url || (data.publicUrl ? `${STATIC_FILE_SERVER_BASE}${data.publicUrl}` : null);
    }
    return null;
  } catch {
    return null;
  }
}

// ============================================================================
// Types
// ============================================================================

interface StakeholderPeopleTabProps {
  workItem: WorkItem;
  isDark: boolean;
  onRefresh?: () => void;
  onGrantPortalAccess?: (person: StakeholderPerson) => void;
  contactPortalEnabled?: boolean;
}

interface ContactPerson {
  id?: number;
  contact_id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  job_title: string | null;
  department: string | null;
  is_primary: boolean;
  is_active: boolean;
  notes: string | null;
  default_location_id?: number | null;
  photo_url?: string | null;
  notifications?: ContactPersonNotifications;
  has_whatsapp?: boolean | null;
  whatsapp_verified_at?: string | null;
}

interface StakeholderPerson {
  id: number;
  contact_id: number;
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  job_title?: string | null;
  department?: string | null;
  is_primary?: boolean;
  is_active?: boolean;
  notes?: string | null;
  default_location_id?: number | null;
  default_location_name?: string | null;
  contact_company_name?: string | null;
  photo_url?: string | null;
  notifications?: ContactPersonNotifications;
  has_whatsapp?: boolean | null;
  whatsapp_verified_at?: string | null;
  has_portal_access?: boolean | null;
  portal_role?: string | null;
  assigned_locations?: Array<{
    location_id: number;
    location_name: string;
    city?: string;
    role_at_location?: string;
    is_primary?: boolean;
  }>;
}

interface LocationAssignment {
  location_id: number;
  is_primary: boolean;
  role_at_location: string;
}

interface OnRouteNotifications {
  whatsapp: boolean;
  email: boolean;
  whatsapp_group_jid?: string | null;
  whatsapp_group_invite_link?: string | null;
}

interface OnSiteNotifications {
  whatsapp: boolean;
  email: boolean;
  whatsapp_group_jid?: string | null;
  whatsapp_group_invite_link?: string | null;
}

interface ContactPersonNotifications {
  on_route: OnRouteNotifications;
  on_site: OnSiteNotifications;
}

// ============================================================================
// Component
// ============================================================================

export default function StakeholderPeopleTab({ workItem, isDark, onRefresh, onGrantPortalAccess, contactPortalEnabled: parentContactPortalEnabled }: StakeholderPeopleTabProps) {
  const { config } = useContactConfig();
  const [searchQuery, setSearchQuery] = useState('');
  const [editingPerson, setEditingPerson] = useState<StakeholderPerson | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState<string>('user');
  const photoInputRef = useRef<HTMLInputElement>(null);
  const lastVerifiedMobileRef = useRef<string | null>(null);

  // Location data for form
  const [contactLocations, setContactLocations] = useState<Array<{ id: number; location_name: string; city?: string }>>([]);
  const [assignedLocations, setAssignedLocations] = useState<LocationAssignment[]>([]);

  // Form state
  const [formData, setFormData] = useState<Partial<ContactPerson>>({});

  // WhatsApp verification state
  const [hasWhatsApp, setHasWhatsApp] = useState<boolean | null>(null);
  const [isVerifyingWhatsApp, setIsVerifyingWhatsApp] = useState(false);

  // Customer portal enabled state
  const [customerPortalEnabled, setCustomerPortalEnabled] = useState(true);
  const [contactPortalEnabledState, setContactPortalEnabledState] = useState<boolean | null>(null);
  const effectiveContactPortalEnabled = parentContactPortalEnabled !== undefined ? parentContactPortalEnabled : contactPortalEnabledState;

  // Portal invitation sending state
  const [sendingInvitePersonId, setSendingInvitePersonId] = useState<number | null>(null);
  const [sentInvitePersonIds, setSentInvitePersonIds] = useState<Set<number>>(new Set());

  // WhatsApp group join state
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [qrScannerContext, setQrScannerContext] = useState<'on_route' | 'on_site'>('on_route');
  const [isJoiningGroup, setIsJoiningGroup] = useState(false);

  // Verify WhatsApp number and update database.
  // By default the API route consults the DB cache first (no external WhatsApp
  // call when a fresh entry exists). Pass `forceRefresh: true` to bypass the
  // cache (used by the manual "Re-verify" button).
  const verifyWhatsAppNumber = useCallback(async (phone: string, personId?: number, forceRefresh = false) => {
    if (!phone) {
      setHasWhatsApp(null);
      return;
    }
    setIsVerifyingWhatsApp(true);
    try {
      const result = await validateWhatsAppNumber(phone, forceRefresh);
      const hasWhatsAppResult = result.success && result.isRegistered;
      setHasWhatsApp(hasWhatsAppResult);

      // Update database if we have a person ID (keep contact_people in sync)
      if (personId) {
        try {
          await fetch(`/api/v1/contacts/people/${personId}`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ has_whatsapp: hasWhatsAppResult }),
          });
        } catch (err) {
          console.error('Failed to update WhatsApp status in database:', err);
        }
      }
    } catch (error) {
      console.error('WhatsApp verification failed:', error);
      setHasWhatsApp(false);
    } finally {
      setIsVerifyingWhatsApp(false);
    }
  }, []);

  // Join WhatsApp group via invite link and store resulting JID
  const handleJoinGroup = useCallback(async (context: 'on_route' | 'on_site' = 'on_route') => {
    const inviteLink = formData.notifications?.[context]?.whatsapp_group_invite_link || '';
    const inviteCode = extractWhatsAppInviteCode(inviteLink);

    if (!inviteCode) {
      notify.error('Invalid WhatsApp invite link. Please provide a valid https://chat.whatsapp.com/... link or invite code.');
      return;
    }

    setIsJoiningGroup(true);
    try {
      const token = getAuthToken();
      if (!token) {
        notify.error('Authentication token not available.');
        return;
      }

      const res = await fetch('/api/v1/whatsapp/client', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'join-group',
          invite_code: inviteCode,
          mode: 'support',
        }),
      });

      const data = await res.json();
      if (data.success && data.groupJid) {
        const builder = context === 'on_site' ? buildOnSiteNotifications : buildOnRouteNotifications;
        const updatedNotifications = builder(formData.notifications, { whatsapp_group_jid: data.groupJid });
        setFormData((p) => ({ ...p, notifications: updatedNotifications }));
        notify.success(`Joined WhatsApp group: ${data.groupName || data.groupJid}`);

        // Auto-save JID to backend so it persists even if user forgets to Save
        const personId = editingPerson?.id;
        if (personId) {
          try {
            await fetch(`/api/v1/contacts/people/${personId}`, {
              method: 'PATCH',
              headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ notifications: updatedNotifications }),
            });
          } catch (err) {
            console.warn('[StakeholderPeopleTab] Auto-save JID failed:', err);
          }
        }
      } else {
        notify.error(data.error || 'Failed to join WhatsApp group');
      }
    } catch (err) {
      console.error('Join group failed:', err);
      notify.error(err instanceof Error ? err.message : 'Failed to join WhatsApp group');
    } finally {
      setIsJoiningGroup(false);
    }
  }, [formData.notifications, editingPerson]);

  const cf = useMemo(() => {
    if (!workItem.custom_fields) return {} as Record<string, unknown>;
    return typeof workItem.custom_fields === 'string' ? JSON.parse(workItem.custom_fields) : workItem.custom_fields;
  }, [workItem.custom_fields]);

  const contactId = cf.entity_id || workItem.contact_id;
  const showForm = isAdding || !!editingPerson;

  // custom_fields.related_people is a snapshot baked into the virtual work item
  // when the modal opened — the modal's own formData.custom_fields shadows later
  // prop refreshes, so it stays stale (newly-added people needed a hard reload).
  // Fetch the live list from the API instead; the snapshot only seeds the first
  // paint before the fetch resolves.
  const [livePeople, setLivePeople] = useState<StakeholderPerson[] | null>(null);
  const loadPeople = useCallback(async () => {
    if (!contactId) return;
    try {
      const res = await fetch(`/api/v1/contacts/${contactId}/people`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) return; // keep the snapshot fallback on failure
      const json = await res.json();
      setLivePeople(Array.isArray(json?.data) ? json.data : []);
    } catch {
      // Network/auth failure — keep showing the snapshot.
    }
  }, [contactId]);

  const people = useMemo<StakeholderPerson[]>(() => {
    if (livePeople) return livePeople;
    return Array.isArray(cf.related_people) ? cf.related_people : [];
  }, [livePeople, cf.related_people]);

  // Load the live people list once the contact id resolves.
  useEffect(() => {
    void loadPeople();
  }, [loadPeople]);

  // Load user role
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch('/api/v1/auth/me', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.user) setCurrentUserRole(data.user.role || data.user.system_role || 'user');
        }
      } catch { /* ignore */ }
    };
    load();
  }, []);

  // Fetch customer portal enabled state
  useEffect(() => {
    const fetchCustomerPortalStatus = async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          return;
        }

        const response = await fetch('/api/v1/client/settings', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            const portalTemplate = data.data.portal_template || 'classic-grid';
            setCustomerPortalEnabled(portalTemplate !== 'none');
          }
        }
      } catch (error) {
        console.warn('[StakeholderPeopleTab] Failed to fetch customer portal status:', error);
      }
    };

    fetchCustomerPortalStatus();
  }, []);

  // Fetch contact-level portal_enabled
  useEffect(() => {
    if (!contactId) return;
    const fetchContactPortal = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const response = await fetch(`/api/v1/contacts/${contactId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data && typeof data.data.portal_enabled === 'boolean') {
            setContactPortalEnabledState(data.data.portal_enabled);
          }
        }
      } catch {
        // ignore
      }
    };
    fetchContactPortal();
  }, [contactId]);

  // Load contact's locations when form opens
  useEffect(() => {
    if (!showForm || !contactId) return;
    const load = async () => {
      try {
        const res = await fetch(`/api/v1/contacts/${contactId}/locations/`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
        if (res.ok) {
          const { data } = await res.json();
          setContactLocations(data || []);
        }
      } catch { /* ignore */ }
    };
    load();
  }, [showForm, contactId]);

  // Load assigned locations when editing
  useEffect(() => {
    if (!editingPerson?.id) { setAssignedLocations([]); return; }
    const load = async () => {
      try {
        const res = await fetch(`/api/v1/contacts/people/${editingPerson.id}/locations/`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
        if (res.ok) {
          const { data } = await res.json();
          setAssignedLocations(
            data?.map((l: { contact_location_id: number; is_primary: boolean; role_at_location?: string }) => ({ location_id: l.contact_location_id, is_primary: l.is_primary, role_at_location: l.role_at_location || '' })) || []
          );
        }
      } catch { /* ignore */ }
    };
    load();
  }, [editingPerson?.id]);

  const filteredPeople = useMemo(() => {
    if (!searchQuery.trim()) return people;
    const q = searchQuery.toLowerCase();
    return people.filter((p) => {
      const fullName = `${p.first_name} ${p.last_name}`.toLowerCase();
      return fullName.includes(q) || (p.email || '').toLowerCase().includes(q) || (p.phone || '').toLowerCase().includes(q) || (p.mobile || '').toLowerCase().includes(q) || (p.job_title || '').toLowerCase().includes(q) || (p.department || '').toLowerCase().includes(q);
    });
  }, [people, searchQuery]);

  const resetForm = useCallback((person?: StakeholderPerson | null) => {
    if (person) {
      setFormData({
        contact_id: person.contact_id || contactId,
        first_name: person.first_name || '',
        last_name: person.last_name || '',
        email: person.email || '',
        phone: person.phone || '',
        mobile: person.mobile || '',
        job_title: person.job_title || '',
        department: person.department || '',
        is_primary: person.is_primary || false,
        is_active: person.is_active ?? true,
        default_location_id: person.default_location_id || null,
        notes: person.notes || '',
        photo_url: person.photo_url || null,
        notifications: (person.notifications as ContactPersonNotifications) || { on_route: { whatsapp: false, email: false, whatsapp_group_jid: null, whatsapp_group_invite_link: null }, on_site: { whatsapp: false, email: false, whatsapp_group_jid: null, whatsapp_group_invite_link: null } },
        has_whatsapp: person.has_whatsapp,
        whatsapp_verified_at: person.whatsapp_verified_at,
      });
      // Use stored WhatsApp status, only verify if never verified
      setHasWhatsApp(person.has_whatsapp ?? null);
    } else {
      setFormData({
        contact_id: contactId,
        first_name: '', last_name: '', email: '', phone: '', mobile: '',
        job_title: '', department: '', is_primary: false, is_active: true,
        default_location_id: null, notes: '', photo_url: null,
        notifications: { on_route: { whatsapp: false, email: false, whatsapp_group_jid: null, whatsapp_group_invite_link: null }, on_site: { whatsapp: false, email: false, whatsapp_group_jid: null, whatsapp_group_invite_link: null } },
      });
      setAssignedLocations([]);
      setHasWhatsApp(null);
    }
    lastVerifiedMobileRef.current = null;
  }, [contactId]);

  // Auto-verify WhatsApp whenever mobile number changes.
  // When editing an existing person whose mobile is unchanged and whose DB
  // has_whatsapp value is fresh (<= 30 days), use the stored value directly
  // and skip the API call entirely. Only call the API for new/changed numbers
  // (the API route itself also consults the DB cache, so this is a double win).
  useEffect(() => {
    if (formData.mobile && formData.mobile !== lastVerifiedMobileRef.current) {
      const person = editingPerson;
      const storedMobile = person?.mobile;
      const storedHasWhatsApp = person?.has_whatsapp;
      const storedVerifiedAt = person?.whatsapp_verified_at;

      const isFreshInDb =
        person &&
        storedMobile &&
        storedMobile === formData.mobile &&
        storedHasWhatsApp !== null &&
        storedHasWhatsApp !== undefined &&
        storedVerifiedAt &&
        (Date.now() - new Date(storedVerifiedAt).getTime()) <= 30 * 24 * 60 * 60 * 1000;

      if (isFreshInDb) {
        // Use the stored DB value — no API call needed.
        setHasWhatsApp(storedHasWhatsApp === true);
        lastVerifiedMobileRef.current = formData.mobile;
        return;
      }

      verifyWhatsAppNumber(formData.mobile, editingPerson?.id);
      lastVerifiedMobileRef.current = formData.mobile;
    } else if (!formData.mobile) {
      setHasWhatsApp(null);
      lastVerifiedMobileRef.current = null;
    }
  }, [formData.mobile, editingPerson, verifyWhatsAppNumber]);

  const handleAdd = () => { setEditingPerson(null); resetForm(); setIsAdding(true); };
  const handleEdit = async (person: StakeholderPerson) => {
    setIsAdding(false);
    setEditingPerson(person);
    // Fetch fresh data from API to get latest notifications etc.
    try {
      const res = await fetch(`/api/v1/contacts/people/${person.id}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (res.ok) {
        const { data } = await res.json();
        if (data) {
          resetForm(data as StakeholderPerson);
          return;
        }
      }
    } catch {}
    resetForm(person);
  };
  const handleCancel = () => { setIsAdding(false); setEditingPerson(null); };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file, 'contact-people');
      if (url) { setFormData((p) => ({ ...p, photo_url: url })); notify.success('Photo uploaded'); }
      else notify.error('Failed to upload photo');
    } catch { notify.error('Upload failed'); } finally { setUploading(false); }
  };

  const toggleLocationAssignment = (locationId: number) => {
    setAssignedLocations((prev) => {
      if (prev.find((l) => l.location_id === locationId)) return prev.filter((l) => l.location_id !== locationId);
      return [...prev, { location_id: locationId, is_primary: false, role_at_location: '' }];
    });
  };

  const updateLocationRole = (locationId: number, role: string) => {
    setAssignedLocations((prev) => prev.map((l) => (l.location_id === locationId ? { ...l, role_at_location: role } : l)));
  };

  const handleSave = async () => {
    if (!formData.first_name || !formData.last_name) { notify.error('First name and last name are required'); return; }
    if (config.person_email_required && !formData.email) { notify.error('Email is required'); return; }
    if (config.person_mobile_required && !formData.mobile) { notify.error('Mobile number is required'); return; }

    const personId = editingPerson?.id;
    const cid = formData.contact_id || contactId;
    setLoading(true);
    try {
      const url = personId ? `/api/v1/contacts/${cid}/people/${personId}/` : `/api/v1/contacts/${cid}/people/`;
      const res = await fetch(url, {
        method: personId ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        const result = await res.json();
        const savedId = personId || result.data?.id;
        if (savedId && assignedLocations.length > 0) {
          await fetch(`/api/v1/contacts/people/${savedId}/locations/`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ locations: assignedLocations }),
          });
        }
        notify.success(personId ? 'Person updated' : 'Person created');
        handleCancel();
        await loadPeople();
        onRefresh?.();
      } else {
        const data = await res.json();
        notify.error(data.error || 'Failed to save person');
      }
    } catch { notify.error('Failed to save person'); } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!editingPerson?.id) return;
    if (currentUserRole !== 'admin' && currentUserRole !== 'owner') { notify.error('Only admins and owners can delete people'); return; }
    await confirm({
      title: 'Delete Person',
      message: `Are you sure you want to delete "${editingPerson.first_name} ${editingPerson.last_name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const res = await fetch(`/api/v1/contacts/people/${editingPerson.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getAuthToken()}` } });
          if (res.ok) { notify.success('Person deleted'); handleCancel(); void loadPeople(); onRefresh?.(); }
          else { const d = await res.json(); notify.error(d.error || 'Failed to delete person'); }
        } catch { notify.error('Failed to delete person'); }
      },
    });
  };

  // Send portal invitation to a contact person
  const handleSendPortalInvitation = async (person: StakeholderPerson) => {
    if (!customerPortalEnabled) {
      notify.error('Customer portal is currently disabled for this organisation.');
      return;
    }
    if (effectiveContactPortalEnabled === false) {
      notify.error('Customer portal is disabled for this contact. Enable it in the Overview tab first.');
      return;
    }
    if (!person.email && !person.phone) {
      notify.error('Person must have an email or phone number to receive an invitation.');
      return;
    }

    setSendingInvitePersonId(person.id);
    try {
      const token = getAuthToken();
      if (!token) {
        notify.error('Authentication token not available.');
        return;
      }

      // 1. Update contact person record to enable portal access
      // Map the stakeholder type to the correct portal role:
      //   customer  → customer
      //   supplier  → vendor (suppliers access the vendor portal)
      //   other     → customer (default)
      const portalType = workItem.item_type || 'customer';
      const portalRole = portalType === 'supplier' ? 'vendor' : portalType === 'vendor' ? 'vendor' : 'customer';
      const patchRes = await fetch(`/api/v1/contacts/people/${person.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          has_portal_access: true,
          portal_role: portalRole,
        }),
      });

      if (!patchRes.ok) {
        const errData = await patchRes.json().catch(() => ({}));
        notify.error(errData.error || 'Failed to enable portal access');
        return;
      }

      // 2. Send portal invitation
      const inviteRes = await fetch('/api/v1/portal/invite', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: person.email,
          phone: person.mobile || person.phone,
          onboarding_type: 'contact_portal',
          contact_id: person.contact_id || contactId,
          contact_person_id: person.id,
          portal_type: portalType,
        }),
      });

      const result = await inviteRes.json();
      if (result.success) {
        setSentInvitePersonIds((prev) => new Set(prev).add(person.id));
        notify.success('Portal invitation sent successfully');
        onRefresh?.();
      } else {
        notify.error(result.error || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Portal invitation error:', error);
      notify.error('Failed to send portal invitation');
    } finally {
      setSendingInvitePersonId(null);
    }
  };

  // Style helpers
  const cardBg = isDark ? 'bg-gray-800/60 border-gray-700' : 'bg-white border-gray-200';
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ${isDark ? 'border-gray-600 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'}`;
  const labelClass = `block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`;

  // ---------------------------------------------------------------------------
  // FORM VIEW
  // ---------------------------------------------------------------------------
  if (showForm) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button onClick={handleCancel} className={`flex items-center gap-1 text-sm font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}>
            <ChevronLeft className="w-4 h-4" /> Back to list
          </button>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{editingPerson?.id ? 'Edit Person' : 'New Person'}</h3>
        </div>

        <div className={`rounded-xl border p-5 space-y-5 ${cardBg}`}>
          {/* Photo */}
          {config.person_show_photo && (
            <div className="flex items-center gap-4">
              <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
              <div onClick={() => photoInputRef.current?.click()} className={`w-16 h-16 rounded-full overflow-hidden cursor-pointer border-2 border-dashed flex-shrink-0 ${isDark ? 'border-gray-600 hover:border-blue-400' : 'border-gray-300 hover:border-blue-500'}`}>
                {formData.photo_url ? <img src={formData.photo_url} alt="" className="w-full h-full object-cover" /> : (
                  <div className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin text-blue-500" /> : <Camera className="w-5 h-5 text-gray-400" />}
                  </div>
                )}
              </div>
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Profile Photo</p>
                {formData.photo_url && <button type="button" onClick={() => setFormData((p) => ({ ...p, photo_url: null }))} className="text-xs text-red-500 hover:text-red-600">Remove</button>}
              </div>
            </div>
          )}

          {/* Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelClass}>First Name *</label><input type="text" value={formData.first_name || ''} onChange={(e) => setFormData((p) => ({ ...p, first_name: e.target.value }))} className={inputClass} /></div>
            <div><label className={labelClass}>Last Name *</label><input type="text" value={formData.last_name || ''} onChange={(e) => setFormData((p) => ({ ...p, last_name: e.target.value }))} className={inputClass} /></div>
          </div>

          {/* Job / Dept */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelClass}>Job Title</label><input type="text" value={formData.job_title || ''} onChange={(e) => setFormData((p) => ({ ...p, job_title: e.target.value }))} className={inputClass} placeholder="e.g. Building Manager" /></div>
            <div><label className={labelClass}>Department</label><input type="text" value={formData.department || ''} onChange={(e) => setFormData((p) => ({ ...p, department: e.target.value }))} className={inputClass} placeholder="e.g. Facilities" /></div>
          </div>

          {/* Email / Phone / Mobile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelClass}>Email{config.person_email_required && <span className="text-red-500 ml-1">*</span>}</label><input type="email" value={formData.email || ''} onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))} className={inputClass} /></div>
            {config.person_phone_visible && (
              <div><PhoneInputWithCountryCode label="Phone" value={formData.phone || null} onChange={(v) => setFormData((p) => ({ ...p, phone: v || '' }))} isDark={isDark} showWhatsAppIcon={config.person_show_wa_link} /></div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <PhoneInputWithCountryCode label={`Mobile${config.person_mobile_required ? ' *' : ''}`} value={formData.mobile || null} onChange={(v) => setFormData((p) => ({ ...p, mobile: v || '' }))} isDark={isDark} showWhatsAppIcon={config.person_show_wa_link} />
                </div>
                {formData.mobile && !isVerifyingWhatsApp && (
                  <button
                    type="button"
                    onClick={() => verifyWhatsAppNumber(formData.mobile!, editingPerson?.id, true)}
                    className="mt-6 flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 whitespace-nowrap"
                  >
                    <RefreshCw className="w-3 h-3" />
                    {hasWhatsApp === null ? 'Verify' : 'Re-verify'}
                  </button>
                )}
                {isVerifyingWhatsApp && (
                  <div className="mt-6 flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                    <span className="text-xs text-gray-400">Verifying...</span>
                  </div>
                )}
              </div>
              {hasWhatsApp === true && (
                <div className="mt-1 text-xs text-green-600 dark:text-green-400">✓ WhatsApp verified</div>
              )}
              {hasWhatsApp === false && (
                <div className="mt-1 text-xs text-gray-400">No WhatsApp on this number</div>
              )}
            </div>
          </div>

          {/* Primary Location */}
          <div>
            <label className={labelClass}>Primary Location</label>
            <select value={formData.default_location_id || ''} onChange={(e) => setFormData((p) => ({ ...p, default_location_id: e.target.value ? parseInt(e.target.value) : null }))} className={inputClass}>
              <option value="">No primary location</option>
              {contactLocations.map((l) => <option key={l.id} value={l.id}>{l.location_name}{l.city ? ` (${l.city})` : ''}</option>)}
            </select>
          </div>

          {/* Location Assignments */}
          {contactLocations.length > 0 && (
            <div>
              <label className={labelClass}>Assigned Locations</label>
              <div className={`border rounded-lg divide-y max-h-[200px] overflow-y-auto ${isDark ? 'border-gray-700 divide-gray-700' : 'border-gray-200 divide-gray-200'}`}>
                {contactLocations.map((loc) => {
                  const isAssigned = assignedLocations.some(al => al.location_id === loc.id);
                  const assignment = assignedLocations.find(al => al.location_id === loc.id);
                  return (
                    <label key={loc.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isAssigned}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAssignedLocations([...assignedLocations, { location_id: loc.id, is_primary: false, role_at_location: '' }]);
                          } else {
                            setAssignedLocations(assignedLocations.filter(al => al.location_id !== loc.id));
                          }
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className={`flex-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{loc.location_name}{loc.city ? ` (${loc.city})` : ''}</span>
                      {isAssigned && (
                        <input
                          type="text"
                          placeholder="Role"
                          value={assignment?.role_at_location || ''}
                          onChange={(e) => {
                            setAssignedLocations(assignedLocations.map(al =>
                              al.location_id === loc.id ? { ...al, role_at_location: e.target.value } : al
                            ));
                          }}
                          className={`w-24 px-2 py-1 text-sm border rounded ${isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-900'}`}
                        />
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notes */}
          <div><label className={labelClass}>Notes</label><textarea value={formData.notes || ''} onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))} rows={2} className={inputClass} /></div>

          {/* Checkboxes */}
          <div className="flex gap-6 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.is_primary || false} onChange={(e) => setFormData((p) => ({ ...p, is_primary: e.target.checked }))} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Primary Contact</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.is_active ?? true} onChange={(e) => setFormData((p) => ({ ...p, is_active: e.target.checked }))} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Active</span>
            </label>
          </div>

          {/* On-Route Notifications */}
          <div className={`rounded-lg border p-4 space-y-3 ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50/70 border-gray-200'}`}>
            <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
              On-Route Notifications
            </p>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Choose how this contact should be notified when a technician is on the way.
            </p>
            <div className="flex gap-6 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.notifications?.on_route?.email || false}
                  onChange={(e) => {
                    setFormData((p) => ({
                      ...p,
                      notifications: buildOnRouteNotifications(p.notifications, { email: e.target.checked }),
                    }));
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Mail className="w-4 h-4 text-blue-500" />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Email</span>
              </label>
              {hasWhatsApp === true && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.notifications?.on_route?.whatsapp || false}
                    onChange={(e) => {
                      setFormData((p) => ({
                        ...p,
                        notifications: buildOnRouteNotifications(p.notifications, { whatsapp: e.target.checked }),
                      }));
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="w-4 h-4 text-teal-500 flex items-center justify-center text-xs font-bold">WA</span>
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>WhatsApp</span>
                </label>
              )}
            </div>

            {/* WhatsApp Group configuration (only when WhatsApp on-route is enabled) */}
            {hasWhatsApp === true && formData.notifications?.on_route?.whatsapp && (
              <div className="space-y-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-3 mt-2">
                <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  WhatsApp Group Notification
                </p>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  By default, on-route notifications go to the contact's direct WhatsApp number. You can redirect them to a WhatsApp group instead.
                </p>

                {/* Invite link input */}
                <div className="space-y-1.5">
                  <label className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Group Invite Link or Code
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.notifications?.on_route?.whatsapp_group_invite_link || ''}
                      onChange={(e) => {
                        setFormData((p) => ({
                          ...p,
                          notifications: buildOnRouteNotifications(p.notifications, { whatsapp_group_invite_link: e.target.value.trim() || null }),
                        }));
                      }}
                      placeholder="https://chat.whatsapp.com/...  or paste invite code"
                      className={`flex-1 px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-teal-500`}
                    />
                    <button
                      type="button"
                      onClick={() => { setQrScannerContext('on_route'); setShowQrScanner(true); }}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                      title="Scan QR code"
                    >
                      <Camera className="w-4 h-4" />
                      Scan
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleJoinGroup('on_route')}
                      disabled={isJoiningGroup || !formData.notifications?.on_route?.whatsapp_group_invite_link}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isJoiningGroup ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      Join Group
                    </button>
                    {formData.notifications?.on_route?.whatsapp_group_jid && (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-mono ${isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-700'}`}>
                        <CheckCircle className="w-3 h-3" />
                        {formData.notifications.on_route.whatsapp_group_jid}
                      </span>
                    )}
                  </div>
                </div>

                {/* Manual group JID override */}
                <div className="space-y-1.5 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Already know the group JID?
                  </label>
                  <input
                    type="text"
                    value={formData.notifications?.on_route?.whatsapp_group_jid || ''}
                    onChange={(e) => {
                      setFormData((p) => ({
                        ...p,
                        notifications: buildOnRouteNotifications(p.notifications, { whatsapp_group_jid: e.target.value.trim() || null }),
                      }));
                    }}
                    placeholder="e.g. 1234567890@g.us — leave blank for direct message"
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-teal-500`}
                  />
                </div>
              </div>
            )}
          </div>

          {/* On-Site Notifications */}
          <div className={`rounded-lg border p-4 space-y-3 ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50/70 border-gray-200'}`}>
            <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
              On-Site Notifications
            </p>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Choose how this contact should be notified when a technician arrives on site or checks in.
            </p>
            <div className="flex gap-6 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.notifications?.on_site?.email || false}
                  onChange={(e) => {
                    setFormData((p) => ({
                      ...p,
                      notifications: buildOnSiteNotifications(p.notifications, { email: e.target.checked }),
                    }));
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Mail className="w-4 h-4 text-blue-500" />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Email</span>
              </label>
              {hasWhatsApp === true && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.notifications?.on_site?.whatsapp || false}
                    onChange={(e) => {
                      setFormData((p) => ({
                        ...p,
                        notifications: buildOnSiteNotifications(p.notifications, { whatsapp: e.target.checked }),
                      }));
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="w-4 h-4 text-teal-500 flex items-center justify-center text-xs font-bold">WA</span>
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>WhatsApp</span>
                </label>
              )}
            </div>

            {/* WhatsApp Group configuration (only when WhatsApp on-site is enabled) */}
            {hasWhatsApp === true && formData.notifications?.on_site?.whatsapp && (
              <div className="space-y-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-3 mt-2">
                <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  WhatsApp Group Notification
                </p>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  By default, on-site notifications go to the contact's direct WhatsApp number. You can redirect them to a WhatsApp group instead.
                </p>

                {/* Invite link input */}
                <div className="space-y-1.5">
                  <label className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Group Invite Link or Code
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.notifications?.on_site?.whatsapp_group_invite_link || ''}
                      onChange={(e) => {
                        setFormData((p) => ({
                          ...p,
                          notifications: buildOnSiteNotifications(p.notifications, { whatsapp_group_invite_link: e.target.value.trim() || null }),
                        }));
                      }}
                      placeholder="https://chat.whatsapp.com/...  or paste invite code"
                      className={`flex-1 px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-teal-500`}
                    />
                    <button
                      type="button"
                      onClick={() => { setQrScannerContext('on_site'); setShowQrScanner(true); }}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                      title="Scan QR code"
                    >
                      <Camera className="w-4 h-4" />
                      Scan
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleJoinGroup('on_site')}
                      disabled={isJoiningGroup || !formData.notifications?.on_site?.whatsapp_group_invite_link}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isJoiningGroup ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      Join Group
                    </button>
                    {formData.notifications?.on_site?.whatsapp_group_jid && (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-mono ${isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-700'}`}>
                        <CheckCircle className="w-3 h-3" />
                        {formData.notifications.on_site.whatsapp_group_jid}
                      </span>
                    )}
                  </div>
                </div>

                {/* Manual group JID override */}
                <div className="space-y-1.5 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Already know the group JID?
                  </label>
                  <input
                    type="text"
                    value={formData.notifications?.on_site?.whatsapp_group_jid || ''}
                    onChange={(e) => {
                      setFormData((p) => ({
                        ...p,
                        notifications: buildOnSiteNotifications(p.notifications, { whatsapp_group_jid: e.target.value.trim() || null }),
                      }));
                    }}
                    placeholder="e.g. 1234567890@g.us — leave blank for direct message"
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-teal-500`}
                  />
                </div>
              </div>
            )}
          </div>

          {/* QR Scanner Modal */}
          {showQrScanner && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className={`relative w-full max-w-md rounded-xl shadow-2xl overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Scan WhatsApp Group QR Code</h3>
                  <button
                    type="button"
                    onClick={() => setShowQrScanner(false)}
                    className={`p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-4">
                  <WebQRScanner
                    onScan={(result) => {
                      const code = extractWhatsAppInviteCode(result);
                      if (code) {
                        const builder = qrScannerContext === 'on_site' ? buildOnSiteNotifications : buildOnRouteNotifications;
                        setFormData((p) => ({
                          ...p,
                          notifications: builder(p.notifications, { whatsapp_group_invite_link: result }),
                        }));
                        setShowQrScanner(false);
                        notify.success('QR code scanned. Click Join Group to connect.');
                      } else {
                        notify.error('Scanned code is not a valid WhatsApp group invite. Please try again.');
                      }
                    }}
                    onClose={() => setShowQrScanner(false)}
                    prompt="Point camera at the WhatsApp group QR code"
                    formats={['QR_CODE']}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
            {editingPerson?.id ? (
              <button onClick={handleDelete} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"><Trash2 className="w-4 h-4" /> Delete</button>
            ) : <div />}
            <div className="flex gap-3">
              <button onClick={handleCancel} className={`px-4 py-2 text-sm font-medium rounded-lg ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}>Cancel</button>
              <button onClick={handleSave} disabled={loading} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingPerson?.id ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // LIST VIEW
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-4" data-tour-id="stakeholder-people-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
          <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{config.contact_people_label || 'People'}</h3>
          <span className={`text-sm px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{people.length}</span>
        </div>
        <button onClick={handleAdd} data-tour-id="stakeholder-people-add" className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"><Plus className="w-4 h-4" /> Add Person</button>
      </div>

      {people.length > 3 && (
        <div className="relative">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by name, email, phone, job title..." data-tour-id="stakeholder-people-search" className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'}`} />
        </div>
      )}

      {filteredPeople.length === 0 ? (
        <div className={`text-center py-12 rounded-xl border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <Users className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{searchQuery ? 'No people match your search' : 'No people added yet'}</p>
          {!searchQuery && <button onClick={handleAdd} className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-700">Add the first person</button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-tour-id="stakeholder-people-list">
          {filteredPeople.map((person) => (
            <div key={person.id} className={`rounded-xl border p-4 cursor-pointer hover:ring-2 hover:ring-blue-500/40 transition-shadow ${cardBg}`} onClick={() => handleEdit(person)}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                  {person.photo_url ? <img src={person.photo_url} alt="" className="w-full h-full object-cover" /> : <span className="text-sm font-bold">{(person.first_name?.[0] || '')}{(person.last_name?.[0] || '')}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{person.first_name} {person.last_name}</span>
                    {person.is_primary && <span className="flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"><Star className="w-3 h-3" /> Primary</span>}
                    {person.is_active === false && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">Inactive</span>}
                  </div>
                  {(person.job_title || person.department) && <p className={`text-xs mt-0.5 truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{[person.job_title, person.department].filter(Boolean).join(' · ')}</p>}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                    {person.email && <span className={`flex items-center gap-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><Mail className="w-3 h-3" /> {person.email}</span>}
                    {(person.mobile || person.phone) && <PhoneDisplayWithWhatsApp phone={(person.mobile || person.phone)!} className="text-xs" initialHasWhatsApp={person.has_whatsapp ?? null} />}
                  </div>
                  {person.assigned_locations && person.assigned_locations.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {person.assigned_locations.slice(0, 3).map((loc) => (
                        <span key={loc.location_id} className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}><MapPin className="w-2.5 h-2.5" /> {loc.location_name}</span>
                      ))}
                      {person.assigned_locations.length > 3 && <span className="text-[10px] text-gray-500">+{person.assigned_locations.length - 3} more</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Edit className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                  {customerPortalEnabled && effectiveContactPortalEnabled !== false && (person.email || person.phone) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onGrantPortalAccess) {
                          onGrantPortalAccess(person);
                        } else {
                          handleSendPortalInvitation(person);
                        }
                      }}
                      disabled={sendingInvitePersonId === person.id}
                      className="p-1 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/30 rounded transition-colors disabled:opacity-50"
                      title={
                        sentInvitePersonIds.has(person.id) || person.has_portal_access
                          ? 'Portal invitation sent'
                          : 'Send Portal Invitation'
                      }
                    >
                      {sendingInvitePersonId === person.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : sentInvitePersonIds.has(person.id) || person.has_portal_access ? (
                        <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <Send className="w-3.5 h-3.5" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}